import React, {
  AnchorHTMLAttributes,
  ComponentProps,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { FocusableElement } from "@react-types/shared";
import { LocationDescriptor } from "history";
import { useHotkeys } from "react-hotkeys-hook";
import { Link, LinkProps } from "react-router-dom";
import { Property } from "../../generated/graphql";
import { useOutsideAlerter } from "../../utils/outsideAlerter";
import { Button } from "../Common/Button";
import { ActionButton } from "../Common/Buttons/ActionButton";
import { Body } from "../Common/Typography";
import {
  actionLink,
  ActionList,
  DropdownWrapper,
  LabelOption,
  TableActionWrapper,
} from "./__styles__/DropdownMenu";

type OnClickProps = {
  onClick: (event?: React.MouseEvent<HTMLElement>) => void;
  to?: never;
  href?: never;
  download?: never;
  target?: never;
};

type HrefProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  to?: never;
  onClick?: never;
};

type ToProps = Omit<LinkProps, "to"> & {
  onClick?: never;
  href?: never;
  to: LocationDescriptor;
};

export type ActionOptions = OnClickProps | HrefProps | ToProps;

export type ActionsProps = {
  label: string | JSX.Element;
  variant?: Exclude<
    NonNullable<Parameters<typeof actionLink>[0]>["variant"],
    "disabled"
  >;
  lineWrap?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  closeOnClick?: boolean;
  track?: () => void;
} & ActionOptions;

export const getTextColor = (action: ActionsProps) =>
  action.disabled ? "contentDisabled"
  : action.variant === "red" ? "contentCritical"
  : action.variant === "contentDark" ? "contentPrimaryInverted"
  : "contentPrimary";

export type ViewOnMapProperty = {
  id?: Maybe<string>;
  streetAddress?: Maybe<string>;
  longitude?: Maybe<number>;
  latitude?: Maybe<number>;
};

export type ViewOnMapCustomMapGeometry = {
  id?: Maybe<string>;
  customMapId?: Maybe<string>;
};

export const shouldShowViewOnMapActionForCustomMapGeometry = (
  customMapGeometry?: Maybe<ViewOnMapCustomMapGeometry>,
): customMapGeometry is Required<ViewOnMapCustomMapGeometry> => {
  return (
    customMapGeometry !== null &&
    customMapGeometry !== undefined &&
    typeof customMapGeometry.id === "string" &&
    typeof customMapGeometry.customMapId === "string"
  );
};

export const shouldShowViewOnMapActionForProperty = (
  property?: Maybe<ViewOnMapProperty>,
): property is Required<Pick<Property, "id" | "latitude" | "longitude">> &
  Pick<Property, "streetAddress" | "fullAddress"> => {
  return (
    property !== null &&
    property !== undefined &&
    typeof property.id === "string" &&
    typeof property.latitude === "number" &&
    typeof property.longitude === "number"
  );
};

export type DropdownPosition =
  | "below-right"
  | "below-left"
  | "above-right"
  | "above-left";

const getPositionStyles = (position: DropdownPosition, refHeight: number) => {
  if (position === "below-right") {
    return {
      marginTop: "8px",
    };
  } else if (position === "below-left") {
    return {
      right: "0px",
      marginTop: "8px",
    };
  } else if (position === "above-right") {
    return {
      transform: `translate(${0}%, -${100}%) translate(0px, -${
        refHeight + 8
      }px)`,
    };
  } else if (position === "above-left") {
    return {
      right: "0px",
      transform: `translate(${0}%, -${100}%) translate(0px, -${
        refHeight + 8
      }px)`,
    };
  }
  return {};
};

export const DropdownOption = ({
  action,
  setIsDropdownOpen,
  isGrouped = false,
}: {
  action: ActionsProps;
  setIsDropdownOpen?: (isOpen: boolean) => void;
  isGrouped?: boolean;
}) => {
  const className = actionLink({
    variant: action.disabled ? "disabled" : action.variant,
    lineWrap: action.lineWrap,
    isGrouped,
    size: "large",
  });

  const onActionClick = ({
    disabled,
    onClick,
    closeOnClick = true,
    track,
  }: {
    disabled?: boolean;
    onClick?: (event?: React.MouseEvent<HTMLElement>) => void;
    closeOnClick?: boolean;
    track?: () => void;
  }) => {
    if (disabled) {
      return;
    }

    track?.();
    onClick?.();

    if (closeOnClick && setIsDropdownOpen) {
      setIsDropdownOpen(false);
    }
  };

  const onClick = () =>
    onActionClick({
      disabled: action.disabled,
      onClick: action.onClick,
      closeOnClick: action.closeOnClick,
      track: action.track,
    });

  const hotkeyRef = useHotkeys<HTMLDivElement>(
    ["space", "return"],
    () => {
      onClick();
    },
    { preventDefault: true },
  );

  const textColor = getTextColor(action);

  if (action.to) {
    return (
      <Link
        className={className}
        to={action.to}
        onClick={onClick}
        target={action.target}
        tabIndex={0}
      >
        <Body size="large" type="regular" color={textColor} as="span">
          {action.label}
        </Body>
      </Link>
    );
  } else if (action.href) {
    return (
      <a
        className={className}
        href={action.href}
        onClick={onClick}
        download={action.download}
        target={action.target}
        tabIndex={0}
      >
        <Body size="large" type="regular" color={textColor} as="span">
          {action.label}
        </Body>
      </a>
    );
  }
  return (
    <div className={className} onClick={onClick} tabIndex={0} ref={hotkeyRef}>
      <Body size="large" type="regular" color={textColor} as="span">
        {action.label}
      </Body>
    </div>
  );
};

interface DropdownMenu {
  actions:
    | Array<ActionsProps>
    | Array<{
        label: string;
        actions: Array<ActionsProps>;
        to?: never;
        href?: never;
        download?: never;
        target?: never;
        onClick?: never;
      }>;
  buttonText?: string;
  disabled?: boolean;
  hidden?: boolean;
  position?: DropdownPosition;
  actionListWidth?: string;
  customButton?: React.FC<{
    onClick: (event?: React.MouseEvent<FocusableElement>) => void;
    disabled?: boolean;
  }>;
  scrollIntoView?: boolean;
  isTableAction?: boolean;
  size?: ComponentProps<typeof Button>["size"];
}

const DropdownMenu = ({
  actions,
  buttonText,
  disabled = false,
  hidden = false,
  position = "below-left",
  customButton,
  scrollIntoView = false,
  actionListWidth = "auto",
  isTableAction = false,
  size = "small",
  ...props
}: DropdownMenu) => {
  const wrapperRef = useRef(null as null | HTMLDivElement);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [refHeight, setRefHeight] = useState(0);

  useOutsideAlerter({
    ref: wrapperRef,
    onOutsideInteraction: () => setIsDropdownOpen(false),
  });

  useLayoutEffect(() => {
    if (wrapperRef.current && wrapperRef.current.clientHeight) {
      const height = wrapperRef.current.clientHeight;
      setRefHeight(height);
    }
  }, []);

  const actionsRef = useRef(null as null | HTMLDivElement);

  useLayoutEffect(() => {
    if (scrollIntoView && isDropdownOpen) {
      const actionList = actionsRef?.current;
      const documentBody = document.body;

      if (actionList && documentBody && actionList instanceof HTMLElement) {
        if (
          actionList.getBoundingClientRect().bottom + actionList.offsetTop >
          documentBody.getBoundingClientRect().height
        ) {
          actionList?.scrollIntoView({
            block: "end",
            behavior: "smooth",
            inline: "nearest",
          });
        }
      }
    }
  }, [scrollIntoView, isDropdownOpen, actionsRef]);

  const isGrouped =
    Array.isArray(actions) &&
    actions.some(
      action => "actions" in action && Array.isArray((action as any).actions),
    );

  const actionButton =
    customButton ?
      customButton({
        onClick: () => setIsDropdownOpen(!isDropdownOpen),
        disabled,
      })
    : buttonText ?
      <Button
        onClick={e => {
          e.preventDefault();
          setIsDropdownOpen(!isDropdownOpen);
        }}
        size={size}
        styleVariant="secondary"
        disabled={disabled}
        data-testid="actions"
        {...props}
      >
        {buttonText}
      </Button>
    : <ActionButton
        data-testid="actions"
        onClick={e => {
          e.preventDefault();
          setIsDropdownOpen(!isDropdownOpen);
        }}
        isDisabled={disabled}
        {...props}
      />;

  const dropdown = (
    <DropdownWrapper ref={wrapperRef} hidden={hidden}>
      {actionButton}

      {isDropdownOpen && (
        <ActionList
          style={getPositionStyles(position, refHeight)}
          css={{ $$actionListWidth: actionListWidth }}
          data-testid="actions-list"
          ref={actionsRef}
        >
          {isGrouped ?
            (
              actions as Array<{
                label: string;
                actions: Array<ActionsProps>;
              }>
            ).map(
              (
                group: { label: string; actions: ActionsProps[] },
                groupIdx: number,
              ) => (
                <React.Fragment key={groupIdx}>
                  <LabelOption>
                    <Body size="small" type="emphasis">
                      {group.label}
                    </Body>
                  </LabelOption>
                  {group.actions.map((action: ActionsProps, i: number) => (
                    <DropdownOption
                      action={action}
                      setIsDropdownOpen={setIsDropdownOpen}
                      key={`action-${groupIdx}-${i}`}
                      isGrouped={true}
                    />
                  ))}
                </React.Fragment>
              ),
            )
          : (actions as Array<ActionsProps>)
              .filter((action: ActionsProps) => !action.hidden)
              .map((action: ActionsProps, i: number) => (
                <DropdownOption
                  action={action}
                  setIsDropdownOpen={setIsDropdownOpen}
                  key={i}
                />
              ))
          }
        </ActionList>
      )}
    </DropdownWrapper>
  );

  if (isTableAction) {
    return <TableActionWrapper>{dropdown}</TableActionWrapper>;
  }
  return dropdown;
};

export default DropdownMenu;
